import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdminCourt, CourtsFilters, Pagination, listCourts, approveCourt, rejectCourt, updateCourt, toggleActive, updateDraftCourt } from '@/admin/api/courts';
import { CourtsRequests } from '@/app/api/courts/requests';

export interface AdminCourtsState {
  items: AdminCourt[];
  pagination: { page: number; pageSize: number; total?: number; pageCount?: number };
  filters: CourtsFilters;
  loading: boolean;
  error?: string;
}

const initialState: AdminCourtsState = {
  items: [],
  pagination: { page: 1, pageSize: 10, total: 0, pageCount: 1 },
  filters: { page: 1, pageSize: 10 },
  loading: false,
};

export const fetchCourts = createAsyncThunk<Pagination<AdminCourt>, void>(
  'adminCourts/fetchCourts',
  async (_arg, thunkApi) => {
    const state = thunkApi.getState() as any;
    const { filters } = state.adminCourts as AdminCourtsState;
    return await listCourts(filters);
  }
);

export const approveCourtThunk = createAsyncThunk<void, { id: number; reason?: string }>(
  'adminCourts/approveCourt',
  async ({ id, reason }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const items = (state.adminCourts?.items ?? []) as AdminCourt[];
    const target = items.find((i) => i.id === id);
    if (target?.isDraft) {
      await CourtsRequests.updateDraftCourtStatus({ draftCourtId: id, decision: true });
    } else {
      await approveCourt(id, { reason });
    }
  }
);

export const rejectCourtThunk = createAsyncThunk<void, { id: number; reason?: string }>(
  'adminCourts/rejectCourt',
  async ({ id, reason }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const items = (state.adminCourts?.items ?? []) as AdminCourt[];
    const target = items.find((i) => i.id === id);
    if (target?.isDraft) {
      await CourtsRequests.updateDraftCourtStatus({ draftCourtId: id, decision: false });
    } else {
      await rejectCourt(id, { reason });
    }
  }
);

export const updateCourtThunk = createAsyncThunk<void, { id: number; payload: Partial<AdminCourt> }>(
  'adminCourts/updateCourt',
  async ({ id, payload }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const items = (state.adminCourts?.items ?? []) as AdminCourt[];
    const target = items.find((i) => i.id === id);
    if (target?.isDraft) {
      await updateDraftCourt(id, payload);
    } else {
      await updateCourt(id, payload);
    }
  }
);

export const toggleActiveThunk = createAsyncThunk<void, { id: number; isActive: boolean }>(
  'adminCourts/toggleActive',
  async ({ id, isActive }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const items = (state.adminCourts?.items ?? []) as AdminCourt[];
    const target = items.find((i) => i.id === id);
    if (target?.isDraft) {
      throw new Error('Cannot toggle a draft court');
    }
    await toggleActive(id, isActive);
  }
);

export const changeLogoThunk = createAsyncThunk<void, { id: number; logoUrl: string }>(
  'adminCourts/changeLogo',
  async ({ id, logoUrl }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const items = (state.adminCourts?.items ?? []) as AdminCourt[];
    const target = items.find((i) => i.id === id);
    const payload: Partial<AdminCourt> = { imagesUrls: [logoUrl] };
    if (target?.isDraft) {
      await updateDraftCourt(id, payload);
    } else {
      await updateCourt(id, payload);
    }
  }
);

const adminCourtsSlice = createSlice({
  name: 'adminCourts',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<CourtsFilters>>) {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage(state, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.filters.pageSize = action.payload;
      state.filters.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourts.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCourts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
          pageCount: action.payload.pageCount,
        };
      })
      .addCase(fetchCourts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load courts';
      })
      .addCase(approveCourtThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        const item = state.items.find((i) => i.id === id);
        if (item) {
          item.adminStatus = 'ACTIVE';
          item.isDraft = false;
        }
      })
      .addCase(rejectCourtThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        const item = state.items.find((i) => i.id === id);
        if (item) item.adminStatus = 'DECLINED';
      })
      .addCase(toggleActiveThunk.fulfilled, (state, action) => {
        const { id, isActive } = action.meta.arg as { id: number; isActive: boolean };
        const item = state.items.find((i) => i.id === id);
        if (item) item.isActive = isActive;
      })
      .addCase(changeLogoThunk.fulfilled, (state, action) => {
        const { id, logoUrl } = action.meta.arg as { id: number; logoUrl: string };
        const item = state.items.find((i) => i.id === id);
        if (item) {
          // Reflect image change locally; API replaces images list.
          item.imagesUrls = [logoUrl];
        }
      })
      .addCase(updateCourtThunk.fulfilled, (state, action) => {
        const { id, payload } = action.meta.arg as { id: number; payload: Partial<AdminCourt> };
        const idx = state.items.findIndex((i) => i.id === id);
        if (idx >= 0) state.items[idx] = { ...state.items[idx], ...payload } as AdminCourt;
      });
  },
});

export const { setFilters, setPage, setPageSize } = adminCourtsSlice.actions;
export default adminCourtsSlice;
